/**
 * Extracting — in-flight progress view shown while a questionnaire's
 * extract/match/draft pipeline runs (PR-P2-3).
 *
 * Not a routed page — QuestionnaireNew.tsx renders this in place of the
 * create form (paste submit) or the mapping modal (upload's confirm +
 * extract step) while that single request is in flight. The "Back to
 * questionnaires" exit link lives in QuestionnaireNew.tsx (it already
 * owns `listHref`/navigation) — this component stays presentational.
 *
 * Reduced scope vs the UC-5 R2 mockup
 * (.claude/skills/ux-adversary/runs/uc-5-questionnaires/questionnaire-new.extracting.web.html):
 * the mockup shows a live 4-segment EXTRACT/MATCH/DRAFT/REVIEW stepper,
 * a real percentage bar, a running "Extracted 27/40" count, and a
 * "~18s remaining" ETA. None of that is real here — the shipped backend
 * runs create -> extract -> match -> draft as ONE synchronous request
 * (packages/cloudflare/src/routes/questionnaires.ts); there is no
 * intermediate signal to poll (confirmed: extractor.process() only
 * calls updateStatus() once, at the end, on success). Rendering fake
 * per-stage/percentage/ETA precision would be presenting information
 * the app doesn't have — the same "dead prop" mistake PR-P2-2's
 * ux-adversary pass caught with the Deadline chip. This screen shows
 * only what's honestly true: elapsed time, and that it's safe to leave.
 *
 * "Safe to leave" is true, not just reassuring copy: the fetch already
 * in flight isn't tied to this component's lifecycle (unmounting
 * doesn't abort it), and Cloudflare Workers continue executing a
 * request handler after the client disconnects — the row will finish
 * processing and show up in the list either way.
 *
 * A real live-progress version needs an async backend split (the
 * paste/confirm route returns immediately, extract/match/draft move to
 * a Cloudflare Workflow, the client polls GET /:id) — a genuine
 * architecture change out of this PR's frontend-only scope, and a
 * decision the user made explicitly for this PR (asked directly;
 * chose the honest-indeterminate frontend version over either building
 * the async backend now or shipping fake precision numbers). Deferred;
 * see docs/implementation-plan.md §3.3 deviation notes and Appendix F.
 *
 * ux-adversary critique pass (2026-08-27, Marcus persona + aesthetic
 * critics) fixed here: the keyframe moved out of a page-local <style>
 * tag into index.css, where Contract C14's lint scanner (which only
 * walks .css files) can actually see it (aesthetic High — it worked
 * today only by accident, via an unrelated blanket override); the
 * ticking elapsed counter no longer shares a live region with the
 * static reassurance sentence, which used to force a full-sentence
 * screen-reader re-announcement every second (aesthetic High); type
 * scale bumped so the one truly load-bearing line (proof of progress +
 * permission to leave) isn't also the smallest, dimmest text on the
 * card (aesthetic High); card border no longer doubles up on --accent
 * with the progress fill (aesthetic Medium); bespoke 6px bar height
 * replaced with a spacing token (aesthetic Medium); reassurance copy
 * now escalates past 90s so a long-but-healthy wait doesn't read as
 * stalled (Marcus Medium); redundant "Extracting" wording (already in
 * the PageHeader above this component) dropped from the card headline
 * (aesthetic Medium).
 */

import { useEffect, useState } from 'react';

const LONG_WAIT_MS = 90_000;

export function Extracting({ title }: { title: string }) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const id = window.setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);
    return () => window.clearInterval(id);
  }, []);

  const elapsedSeconds = Math.floor(elapsedMs / 1000);
  const elapsedLabel = elapsedSeconds < 60 ? `${elapsedSeconds}s` : `${Math.floor(elapsedSeconds / 60)}m ${elapsedSeconds % 60}s`;
  const longWait = elapsedMs > LONG_WAIT_MS;

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-5)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      {/* One-time announcement for screen readers on mount — never
          updates, so it never re-fires. The visible headline below
          covers sighted users; this exists purely so assistive tech
          gets told once that extraction started. .sr-only is the
          design system's existing visually-hidden utility
          (direction-b.css). */}
      <span role="status" className="sr-only">
        Extracting {title}. This can take a minute or more for larger files.
      </span>

      <div>
        <strong style={{ color: 'var(--text)', fontSize: 'var(--font-size-sm)' }}>{title}</strong>
      </div>

      {/* Indeterminate — a filled percentage would claim a precision
          this screen doesn't have. Keyframe + reduced-motion coverage
          live in index.css (see that file's comment on this rule). */}
      <div
        aria-hidden="true"
        style={{
          height: 'var(--space-2)',
          borderRadius: 'var(--radius-chip)',
          background: 'var(--border)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: '40%',
            background: 'var(--accent)',
            borderRadius: 'var(--radius-chip)',
            animation: 'qn-extract-indeterminate 1.6s ease-in-out infinite',
          }}
        />
      </div>

      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
        {longWait
          ? 'Still working — larger or messier files can take a few minutes. Still safe to leave; check back on the list whenever.'
          : 'Detecting questions, matching them to your register, and drafting responses. Larger files can take a minute or more.'}
      </p>
      {/* Not a live region — ticks every second, and re-announcing the
          reassurance sentence on every tick would be the opposite of
          reassuring for screen-reader users (aesthetic High). Visible
          but silent; the one-time status span above covers SR users. */}
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
        {elapsedLabel} elapsed · you can safely close this tab or navigate away — the questionnaire
        will finish processing and appear in your list either way.
      </p>
    </div>
  );
}
